import { WarningCircleIcon } from '@phosphor-icons/react';
import React, { useDeferredValue, useMemo, useRef } from 'react';

import { Response } from '@/components/ai-elements/response';
import { Button } from '@/components/ui/button';
import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface DocumentBodyProps {
  markdown: string;
  isStreaming?: boolean;
  hasError?: boolean;
  errorDetail?: string;
  onRetry?: () => void;
  presentation: 'panel' | 'dialog' | 'workspace';
  isLongForm?: boolean;
  /** Applied to the outer scroll container — for a caller that supplies its own scroll region and only needs this component's own `overflow-y-auto` suppressed or its edges adjusted. */
  className?: string;
  /**
   * Applied to the INNER measure div — the one carrying `panel`'s own
   * `px-8 lg:px-10 py-8` (or `dialog`/`workspace`'s equivalents). Distinct
   * from `className` above, which only reaches the outer scroll wrapper.
   *
   * Two of `panel`'s four callers (`MarkdownCanvas`, `DocumentDrawerBody`)
   * have NO ambient padding of their own and depend on this div's default
   * padding entirely — so that default cannot change. The Desk's
   * `DeskSnapshotBody`, by contrast, already sits inside a Drawer/
   * `FloatingPanel` that supplies its own ambient padding, and `panel`'s
   * default STACKS on top of it: measured live 2026-09-09, the mobile
   * Drawer's article title sat at x=17 (the Drawer's own `px-4`) while the
   * body text started at x=49 (that same 16px plus this div's own 32px) —
   * "the content isn't aligned with the title," in the founder's words.
   * `DeskSnapshotBody` passes `px-0` here for exactly that case; every
   * other caller leaves this unset and gets the unchanged default.
   */
  innerClassName?: string;
  /**
   * The title a caller already shows OUTSIDE this markdown (a `DialogTitle`,
   * a panel's own `<h2>`). When the markdown's own first line is a `# ...`
   * heading matching this string (trimmed, case-insensitive), it is dropped
   * before rendering — the writer pipeline puts the document's title in the
   * markdown itself, and a caller that also displays the title separately
   * would otherwise show it twice. Omit when nothing else displays a title,
   * so the H1 remains the only visible one.
   */
  title?: string;
}

interface TocHeading {
  level: 2 | 3;
  text: string;
  slug: string;
}

/** See `DocumentBodyProps.title`. */
function stripDuplicateTitleHeading(markdown: string, title?: string): string {
  const normalizedTitle = title?.trim().toLowerCase();
  if (!normalizedTitle) return markdown;
  const lines = markdown.split(/\r?\n/);
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  const match = lines[i]?.match(/^#\s+(.+?)\s*$/);
  if (!match || match[1].trim().toLowerCase() !== normalizedTitle) return markdown;
  return lines
    .slice(i + 1)
    .join('\n')
    .replace(/^\s*\n+/, '');
}

export const DocumentBody: React.FC<DocumentBodyProps> = ({
  markdown,
  isStreaming = false,
  hasError,
  errorDetail,
  onRetry,
  presentation,
  isLongForm = false,
  className,
  innerClassName,
  title,
}) => {
  const articleRef = useRef<HTMLElement>(null);
  const displayMarkdown = useMemo(
    () => stripDuplicateTitleHeading(markdown, title),
    [markdown, title],
  );
  const deferredMarkdown = useDeferredValue(displayMarkdown);

  const headings = useMemo(() => {
    if (!isLongForm) return [] as TocHeading[];
    const out: TocHeading[] = [];
    const seen = new Map<string, number>();
    for (const raw of displayMarkdown.split(/\r?\n/)) {
      const m = raw.match(/^(#{2,3})\s+(.+?)\s*$/);
      if (!m) continue;
      const level = m[1].length as 2 | 3;
      const text = m[2].trim();
      const base = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 60);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      const slug = n === 0 ? base : `${base}-${n}`;
      out.push({ level, text, slug });
    }
    return out;
  }, [isLongForm, displayMarkdown]);

  const lineCount = displayMarkdown.split(/\r?\n/).length;
  const isDialog = presentation === 'dialog';
  const isWorkspace = presentation === 'workspace';

  return (
    <div
      className={cn(
        // `size="dialog"` has exactly one caller (`DeskArticleDialog`, via
        // `DeskSnapshotBody`) and it always supplies its own outer scroll
        // region. This wrapper's own `overflow-y-auto` was a second,
        // redundant scroll container nested inside that one — never
        // actually scrolling itself (unconstrained height), but still
        // becoming the TOC `<aside>`'s nearest scrolling ancestor for
        // `position: sticky` purposes, so the sticky offset computed
        // against a container whose own scrollTop never moves. Measured
        // live 2026-09-09: the aside's rect tracked the page scroll 1:1
        // with a permanent zero sticky offset — sticky was inert for the
        // whole 4,493px article, not just past the first screen.
        isDialog ? 'w-full' : 'flex-1 min-h-0 overflow-y-auto',
        className,
      )}
    >
      <div
        className={cn(
          'mx-auto w-full',
          // Workspace is the full-frame reader (editorial "Read article",
          // Library documents): a 720px text measure (`max-w-196` less `lg:px-8`) with room above
          // the title, the Claude Docs proportions the founder pointed at
          // (2026-10-15). Its type lives in `.mora-article`
          // (`src/styles/article-document.css`), shared with the editor.
          isWorkspace
            ? 'px-5 pb-16 pt-10 lg:px-8 lg:pt-16'
            : isDialog
              ? 'px-12 py-6'
              : 'px-8 lg:px-10 py-8',
          isLongForm && headings.length >= 3
            ? 'max-w-[1040px] lg:grid lg:grid-cols-[1fr_200px] lg:gap-10'
            : isWorkspace
              ? 'max-w-196'
              : isDialog
                ? 'max-w-2xl'
                : 'max-w-[680px]',
          innerClassName,
        )}
      >
        {hasError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 mb-6 col-span-full">
            <div className="flex items-start gap-2">
              <WarningCircleIcon className="size-4 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Generation interrupted</p>
                <p className="text-caption text-muted-foreground mt-1">
                  {deferredMarkdown
                    ? 'The response stopped before finishing. Your partial draft is below — retry to pick up where it left off, or copy what you have.'
                    : 'The response stopped before finishing. Retry to pick up where it left off.'}
                </p>
                {errorDetail && (
                  <p className="text-detail text-muted-foreground/80 mt-2 font-mono break-words">
                    {errorDetail}
                  </p>
                )}
                {onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 h-7 text-xs rounded-md"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        {!hasError && !deferredMarkdown && isStreaming ? (
          <article className="min-w-0" aria-busy aria-live="polite">
            <BodyStreamingSkeleton />
          </article>
        ) : deferredMarkdown ? (
          <>
            <article
              ref={articleRef}
              className={cn('min-w-0', isWorkspace && 'mora-article-enter')}
              aria-live={isStreaming ? 'polite' : 'off'}
              aria-busy={isStreaming}
              aria-atomic="false"
            >
              <Response
                className={cn(
                  isWorkspace
                    ? 'mora-article'
                    : 'max-w-none prose-headings:mt-8 first:prose-headings:mt-0 prose-p:leading-7 prose-p:text-body-chat prose-li:leading-7 prose-li:text-body-chat',
                  isDialog && 'prose-h1:text-2xl prose-h2:text-xl prose-h3:text-base',
                  // The headline sits centred over the column, the way a
                  // published article's does; the body stays left-aligned,
                  // because centred prose is unreadable past a line or two.
                  // Founder, 2026-09-07: _"when I pull up the full article, it
                  // has the title left-aligned, when that should be centered."_
                  // H1 ONLY — every H2 and H3 is a scanning landmark down the
                  // left edge, and centring those would destroy the very thing
                  // section headings are for.
                  'prose-h1:text-center prose-h1:text-balance',
                )}
              >
                {deferredMarkdown}
              </Response>
            </article>
            {isLongForm && headings.length >= 3 && (
              <BodyTableOfContents headings={headings} articleRef={articleRef} />
            )}
          </>
        ) : null}
        {isStreaming && deferredMarkdown && (
          <p className="text-detail text-muted-foreground/60 mt-4 col-span-full">
            <BodyStreamingMeta lineCount={lineCount} />
          </p>
        )}
      </div>
    </div>
  );
};

const BodyTableOfContents: React.FC<{
  headings: TocHeading[];
  articleRef: React.RefObject<HTMLElement>;
}> = ({ headings, articleRef }) => {
  const handleClick = (slug: string, text: string) => {
    const scope = articleRef.current;
    if (!scope) return;
    const nodes = scope.querySelectorAll('h2, h3');
    for (const node of Array.from(nodes)) {
      const nodeText = (node.textContent || '').trim();
      if (nodeText === text) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Relative `#slug` alone resolves against `document.baseURI`
        // (`<base href="/">`), not the current path — on the Content
        // Desk's anonymous `/desk/:token` link this silently replaced the
        // token in the address bar with `/#slug`, so a reload, share, or
        // Back landed the client on the app root with no way back in.
        // Measured live, 2026-09-09.
        history.replaceState(null, '', `${location.pathname}${location.search}#${slug}`);
        return;
      }
    }
  };

  return (
    <aside
      className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-6rem)] overflow-y-auto"
      aria-label="Table of contents"
    >
      <div className={cn(SECTION_LABEL, 'mb-3')}>On this page</div>
      {/* Full-opacity `text-muted-foreground` — the `/75` this used to carry
          measured at 2.94:1 in light mode against this card's background,
          below the 4.5:1 floor for 12px interactive text. */}
      <ul className="space-y-1.5 text-caption">
        {headings.map((h) => (
          <li key={h.slug} className={h.level === 3 ? 'pl-3' : undefined}>
            <button
              type="button"
              onClick={() => handleClick(h.slug, h.text)}
              aria-label={`Jump to section: ${h.text}`}
              className="text-left text-muted-foreground hover:text-foreground transition-colors line-clamp-2 leading-snug"
              title={h.text}
            >
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
};

const BodyStreamingMeta: React.FC<{ lineCount: number }> = ({ lineCount }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="size-1.5 rounded-full bg-muted-foreground/40 animate-pulse" />
    <span>
      Generating · {lineCount} line{lineCount === 1 ? '' : 's'}
    </span>
  </span>
);

const BodyStreamingSkeleton: React.FC = () => (
  <div className="space-y-3 animate-pulse">
    <div className="h-4 rounded bg-muted/60 w-2/3" />
    <div className="h-3 rounded bg-muted/40 w-full" />
    <div className="h-3 rounded bg-muted/40 w-11/12" />
    <div className="h-3 rounded bg-muted/40 w-5/6" />
    <div className="h-3 rounded bg-muted/40 w-3/4" />
  </div>
);
